// @ts-check
import { Flex, FlexItem, Accordion, AccordionItem, AccordionToggle, AccordionContent, Spinner } from '@patternfly/react-core';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useSmallAlarmList from 'components/Alarm/useSmallAlarmList';
import AssetCommandList from '../../Command/AssetCommandList';
import TelemetryCardListV2 from '../../TelemetryCard/TelemetryCardListV2';
import AssetPropList from './AssetPropList';
import ModelType from '../../Model/ModelType';
import { useTranslation } from '../../../localization/useTranslation';
import ModelsContainer from '../../Model/ModelsContainer';
import { useTelemetryPushService } from '../../../services/TelemetryApi/TelemetryApi';
import { useConnectionStatus, connectionIcon } from 'utils/hooks/useConnectionStatus';
import styles from './AssetDetails.module.css';

const expandIds = Object.freeze({
  details: 'asset-details-toggle',
  alarms: 'alarm-toggle',
  telemetry: 'telemetry-toggle',
  metaProperties: 'meta-property-toggle',
  properties: 'property-toggle',
  commands: 'command-toggle'
});

const AssetDetails = ({ asset, assetModel, componentModel, updateProps, isUpdatingProps, refetchReported }) => {
  const T = useTranslation();
  const [expanded, setExpanded] = useState([expandIds.details, expandIds.telemetry, expandIds.properties]);
  const telemetryModels = useMemo(() => (!_.isEmpty(componentModel)) ? componentModel.telemetry : assetModel.telemetry, [componentModel, assetModel]);
  const propertyModels = useMemo(() => (!_.isEmpty(componentModel)) ? componentModel.properties : assetModel.properties, [componentModel, assetModel]);
  const commandModels = useMemo(() => (!_.isEmpty(componentModel)) ? componentModel.commands : assetModel.commands, [componentModel, assetModel]);
  const metaPropertyModels = useMemo(() => (!_.isEmpty(componentModel)) ? componentModel.metaProperties : assetModel.metaProperties, [componentModel, assetModel]);
  const hasTelemetry = useMemo(() => !_.isEmpty(telemetryModels), [telemetryModels]);
  const hasProperties = useMemo(() => !_.isEmpty(propertyModels), [propertyModels]);
  const hasCommands = useMemo(() => !_.isEmpty(commandModels), [commandModels]);
  const hasMetaProperties = useMemo(() => !_.isEmpty(metaPropertyModels), [metaPropertyModels]);
  const [telemetryModelValues, isLoading] = useTelemetryPushService(useMemo(() => ({ filterOptions: { assetIds: [asset.id] }, telemetryModels }), [asset.id, telemetryModels]));
  const { connectionStatusList, isLoadingConnectionStatus } = useConnectionStatus([asset]);
  const { SmallAlarmList, hasAlarms } = useSmallAlarmList(asset.id);

  const baseModel = useMemo(() => {
    const model = [
      {
        type: ModelType.TEXT,
        label: T.common.name,
        path: 'displayName',
        isReadOnly: true
      },
      {
        type: ModelType.TEXT,
        label: 'Asset Id',
        path: 'assetId'
      },
      {
        type: ModelType.TEXT,
        label: T.common.modelName,
        path: 'modelName',
        isReadOnly: true
      },
      {
        type: ModelType.TEXT,
        label: T.common.modelId,
        path: 'modelId',
        isReadOnly: true
      },
      {
        type: ModelType.CUSTOM,
        label: T.asset.connectionStatus,
        path: 'connectionStatusList',
        properties: {
          isLoading: isLoadingConnectionStatus,
          render: (value, unusedOnChange, properties) => {
            return (
              <div>
                { properties?.isLoading ?
                  <Spinner size="sm" /> :
                  _.map(value, (connectionStatus, index) => {
                    return (
                      <div key={index}>
                        { connectionIcon(connectionStatus) }
                      </div>
                    );
                  }) }
              </div>
            );
          }
        }
      }
    ];
    const component = [
      {
        type: ModelType.TEXT,
        label: T.common.componentName,
        path: 'componentName',
        isReadOnly: true
      },
      {
        type: ModelType.TEXT,
        label: T.common.modelId,
        path: 'componentModelId',
        isReadOnly: true
      }
    ];
    const description = [{
      type: ModelType.TEXTAREA,
      label: T.common.description,
      path: 'description',
      isReadOnly: true
    }
    ];

    return _.compact([
      ...model,
      ...(componentModel ? component : []),
      ...(asset.description ? description : [])
    ]);
  }, [T.common.name, T.common.modelName, T.common.modelId, T.common.componentName, T.common.description, T.asset.connectionStatus, isLoadingConnectionStatus, componentModel, asset.description]);

  const details = useMemo(() => {
    const details = {
      displayName: asset.displayName,
      assetId: asset.id,
      modelName: assetModel.displayName,
      modelId: asset.modelId,
      componentName: componentModel?.displayName,
      componentModelId: componentModel?.id,
      description: asset.description,
      connectionStatusList
    };
    return details;
  }, [asset.description, asset.displayName, asset.id, asset.modelId, assetModel.displayName, componentModel?.displayName, componentModel?.id, connectionStatusList]);

  const toggle = useCallback((id) => {
    setExpanded(old => {
      const updated = _.xor(old, [id]);
      return updated;
    });
  }, []);
  const expandSection = useCallback((id) => {
    setExpanded(old => {
      const updated = _.union(old, [id]);
      return updated;
    });
  }, []);
  const collapseSection = useCallback((id) => {
    setExpanded(old => {
      const updated = _.without(old, id);
      return updated;
    });
  }, []);

  const Toggle = useCallback(({ id, isExpanded, label, onClick }) => {
    return (
      <AccordionToggle id={id} isExpanded={isExpanded} onClick={() => onClick(id)}>
        <Flex>
          <FlexItem>
            <b>{ label }</b>
          </FlexItem>
          { /* <FlexItem>
            <Spinner size="sm" />
          </FlexItem> */ }
        </Flex>
      </AccordionToggle>
    );
  }, []);

  const Content = useCallback(({ isHidden, children }) => {
    return (
      <AccordionContent isHidden={isHidden}>
        { children }
      </AccordionContent>
    );
  }, []);

  const Item = useCallback(({ id, ids, label, children, toggleClick }) => {
    const included = _.includes(ids, id);
    return (
      <AccordionItem>
        <Toggle id={id} isExpanded={included} label={label} onClick={toggleClick} />
        <Content isHidden={!included}>
          { children }
        </Content>
      </AccordionItem>
    );
  }, []);

  useEffect(() => {
    if (hasAlarms) {
      expandSection(expandIds.alarms);
    } else {
      collapseSection(expandIds.alarms);
    }
  }, [hasAlarms, expandSection, collapseSection]);

  useEffect(() => {
    if (hasMetaProperties) {
      expandSection(expandIds.metaProperties);
    }
  }, [hasAlarms, expandSection, collapseSection, hasMetaProperties]);


  return (
    <div className={styles.assetDetails}>
      <Accordion asDefinitionList={false}>
        <Item id={expandIds.details} ids={expanded} label={T.common.details} toggleClick={toggle} >
          <Flex>
            <FlexItem>
              <ModelsContainer models={baseModel} data={details} setData={()=> {}}>
                {
                  (renderModel) => {
                    return (
                      <>
                        <Flex className="form-group-wrapper" flexWrap={{ default: 'wrap' }} >
                          <FlexItem>
                            { renderModel('displayName') }
                          </FlexItem>
                          <FlexItem>
                            { renderModel('assetId') }
                          </FlexItem>
                          <FlexItem>
                            { renderModel('connectionStatusList') }
                          </FlexItem>
                        </Flex>
                        <Flex className="form-group-wrapper" flexWrap={{ default: 'wrap' }} >
                          <FlexItem>
                            { renderModel('modelName') }
                          </FlexItem>
                          <FlexItem>
                            { renderModel('modelId') }
                          </FlexItem>
                        </Flex>
                        <Flex className="form-group-wrapper" flexWrap={{ default: 'wrap' }} >
                          <FlexItem>
                            { renderModel('componentName') }
                          </FlexItem>
                          <FlexItem>
                            { renderModel('componentModelId') }
                          </FlexItem>
                        </Flex>
                        <Flex className="form-group-wrapper" direction={{ default: 'column' }} >
                          <FlexItem>
                            { renderModel('description') }
                          </FlexItem>
                        </Flex>
                      </>
                    );
                  }
                }
              </ModelsContainer>
            </FlexItem>
          </Flex>
        </Item>
        <Item id={expandIds.alarms} ids={expanded} label={T.common.alarms} toggleClick={toggle} >
          <SmallAlarmList
            assetId={asset.id}
            telemetryModel={assetModel.telemetry}
          />
        </Item>
        { (hasTelemetry && <Item id={expandIds.telemetry} ids={expanded} label={T.common.telemetry} toggleClick={toggle} >
          <TelemetryCardListV2 assetId={asset.id} telemetryModelValues={telemetryModelValues} isLoading={isLoading}/>
        </Item>) }
        { (hasMetaProperties && <Item id={expandIds.metaProperties} ids={expanded} label="Meta Properties" toggleClick={toggle} >
          <AssetPropList
            propModels={metaPropertyModels}
            moduleId={componentModel?.id}
            updateProps={updateProps}
            isUpdatingProps={isUpdatingProps}
            refetchReported={refetchReported}
          />
        </Item>) }
        { (hasProperties && <Item id={expandIds.properties} ids={expanded} label={T.common.properties} toggleClick={toggle} >
          <AssetPropList
            propModels={propertyModels}
            moduleId={componentModel?.id}
            updateProps={updateProps}
            isUpdatingProps={isUpdatingProps}
            refetchReported={refetchReported}
          />
        </Item>) }
        { (hasCommands && <Item id={expandIds.commands} ids={expanded} label={T.common.commands} toggleClick={toggle} >
          <AssetCommandList
            assetId={asset.id}
            commands={commandModels}
          />
        </Item>) }
      </Accordion>
    </div>
  );
};
export default AssetDetails;
